import React, {
	type RefObject,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { TrackType } from '../../../api/cc-smarts/models/TrackType';
import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useRemoveTrack } from '../../hooks/useRemoveTrack';
import { setActiveContent } from '../../state/AudioPlayerState';
import type { FlagsContainer } from '../audio-player/AudioContainer';
import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import type { PlaylistQuery as PlaylistQueryType } from './__generated__/PlaylistQuery.graphql';
import { FooterMessage } from './FooterMessage';
import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistErrorView } from './PlaylistErrorView';
import { PlaylistHeader } from './PlaylistHeader';
import { type PlaylistItemData } from './PlaylistItem';
import { PlaylistLoadingView } from './PlaylistLoadingView';
import { PlaylistView } from './PlaylistView';

const messages = defineMessages({
	removeSuccess: {
		id: 'ai-audio.playlist.remove-success',
		defaultMessage: 'Removed from playlist',
		description: 'Success message shown when an item is removed from the playlist',
	},
	removeError: {
		id: 'ai-audio.playlist.remove-error',
		defaultMessage: 'Something went wrong',
		description: 'Error message shown when an item fails to be removed from the playlist',
	},
	removeSuccessDescription: {
		id: 'ai-audio.playlist.remove-success-description',
		defaultMessage: '{title} has been removed from your playlist.',
		description: 'Description shown when an item is removed from the playlist',
	},
	removeErrorDescription: {
		id: 'ai-audio.playlist.remove-error-description',
		defaultMessage: 'Failed to remove item from playlist',
		description: 'Description shown when an item fails to be removed from the playlist',
	},
});

const styles = cssMap({
	wrapper: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overflow'),
		position: 'absolute',
		bottom: token('space.100'),
		left: token('space.100'),
		right: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	contentContainer: {
		height: '406px',
		position: 'relative',
	},
	content: {
		overflowY: 'auto',
		position: 'relative',
		flex: 1,
		minHeight: '0px',
	},
	footerContainer: {
		flexShrink: 0,
	},
	fullHeightWrapper: {
		flexGrow: 1,
	},
	coverBorder: {
		position: 'absolute',
		width: '100%',
		top: '0',
		backgroundColor: token('elevation.surface'),
		zIndex: 200,
		height: '12px',
	},
	stickyShadow: {
		position: 'sticky',
		top: token('space.negative.050'),
		zIndex: 1,
		boxShadow: token('elevation.shadow.overflow'),
		backgroundColor: token('elevation.surface'),
		height: '5px',
	},
	minifiedAudioPlayer: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
});

type PlaylistWrapperProps = {
	children: React.ReactNode;
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
	audioRef: RefObject<HTMLAudioElement>;
	onExpand?: () => void;
};

const PlaylistWrapper = ({
	children,
	defaultExpanded = false,
	hasOtherActivePlayback = false,
	audioRef,
	onExpand,
}: PlaylistWrapperProps) => {
	const resizingHeightProps = useResizingHeight();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const { activeContent, clearPendingContent } = useAudioPlayerStore();

	const handleToggle = () => {
		setIsExpanded((prev: boolean) => {
			const nextState = !prev;
			if (nextState) {
				onExpand?.(); //only fire the update on the second expand to avoid double network calls
			}
			return nextState;
		});
	};

	const handleMinifiedPlayerClick = () => {
		clearPendingContent();
	};

	const shouldShowMinifiedPlayer = !isExpanded && hasOtherActivePlayback;

	const hasUnavailableItems = true;

	const [isFooterDismissed, setIsFooterDismissed] = useState(false);
	const handleCloseFooter = () => setIsFooterDismissed(true);

	return (
		<Flex direction="column" xcss={styles.wrapper}>
			<PlaylistHeader isExpanded={isExpanded} onToggle={handleToggle} />
			{shouldShowMinifiedPlayer && (
				<Box xcss={styles.minifiedAudioPlayer}>
					<MinifiedAudioPlayer
						title={activeContent.title || ''}
						byline=""
						avatarUrl={''}
						elevated
						audioRef={audioRef}
						onClick={handleMinifiedPlayerClick}
					/>
				</Box>
			)}
			<div {...resizingHeightProps}>
				{isExpanded && (
					<Flex xcss={styles.contentContainer} direction="column">
						<Flex xcss={styles.content} direction="column">
							{/* Scroll shadow effect */}
							<Box xcss={styles.coverBorder} />
							<Flex direction="column" xcss={styles.fullHeightWrapper}>
								<Box xcss={styles.stickyShadow} />
								{children}
							</Flex>
						</Flex>
						{hasUnavailableItems && !isFooterDismissed && (
							<FooterMessage onClose={handleCloseFooter} />
						)}
					</Flex>
				)}
			</div>
		</Flex>
	);
};

type PlaylistInnerProps = {
	cloudId: string;
	audioRef: RefObject<HTMLAudioElement>;
	flags?: FlagsContainer;
	onSetFetchKeyCallback?: (callback: () => void) => void;
};

const PlaylistInner = ({ cloudId, audioRef, flags, onSetFetchKeyCallback }: PlaylistInnerProps) => {
	const [fetchKey, setFetchKey] = useState(0);
	const isFirstExpandRef = useRef(true);

	// Register the increment callback with the parent
	useEffect(() => {
		onSetFetchKeyCallback?.(() => {
			if (isFirstExpandRef.current) {
				isFirstExpandRef.current = false;
				return;
			}
			setFetchKey((prev) => prev + 1);
		});
	}, [onSetFetchKeyCallback]);
	const { fetchAudio } = useAudioPlayerStreamingContext();
	const { activeContent } = useAudioPlayerStore();
	const { formatMessage } = useIntl();
	const { removeTrack } = useRemoveTrack();

	const data = useLazyLoadQuery<PlaylistQueryType>(
		graphql`
			query PlaylistQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainSource
							mainContent {
								id
								title
								type
								subType
								space {
									key
								}
								history {
									createdBy {
										displayName
									}
								}
								properties(keys: ["emoji-title-published"]) {
									nodes {
										value
									}
								}
							}
							supportingSources
						}
					}
				}
			}
		`,
		{ cloudId },
		{
			fetchPolicy: fetchKey > 0 ? 'network-only' : 'store-or-network',
			fetchKey,
		},
	);

	const playlistItems: PlaylistItemData[] = useMemo(() => {
		const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];
		return tracks.map((track: any) => {
			const mainContent = track.mainContent;
			const creator = mainContent?.history?.createdBy;

			return {
				id: mainContent?.id, // Use mainContent.id as the content ID
				title: mainContent?.title || undefined,
				type: track.type,
				author: creator?.displayName || undefined,
				spaceKey: mainContent?.space?.key || '',
				contentType: mainContent?.type || '',
				isLive: !!(mainContent?.subType === 'live'),
				emojiId: mainContent?.properties?.nodes?.[0]?.value || undefined,
				contentId: mainContent?.id,
				mainSource: track.mainSource,
				supportingSources: track.supportingSources || [],
			};
		});
	}, [data]);

	const currentItem = useMemo(() => {
		if (!activeContent?.contentId) {
			return null;
		}
		return playlistItems.find((item) => item.id === activeContent.contentId) || null;
	}, [activeContent?.contentId, playlistItems]);

	const handleItemClick = useCallback(
		(item: PlaylistItemData) => {
			setActiveContent({
				contentId: item.id,
				spaceKey: item.spaceKey || '',
				contentType: item.contentType || '',
				title: item.title,
				author: item.author,
				avatarUrl: '', // TODO: Add avatar URL when available
			});
			fetchAudio();
		},
		[fetchAudio],
	);

	const handleRemoveItem = useCallback(
		async (item: PlaylistItemData) => {
			if (!item.mainSource) {
				return;
			}

			try {
				const mainSource = item.mainSource;
				const supportingSources = item.supportingSources || [];
				const trackType = item.type as TrackType;

				await removeTrack(cloudId, mainSource, supportingSources, trackType);

				flags?.showFlag({
					title: formatMessage(messages.removeSuccess),
					description: formatMessage(messages.removeSuccessDescription, {
						title: item.title,
					}),
					icon: (
						<StatusSuccessIcon
							spacing="spacious"
							color={token('color.icon.success')}
							label="success"
						/>
					),
					type: 'success',
					close: 'auto',
				});
			} catch (error) {
				flags?.showFlag({
					title: formatMessage(messages.removeError),
					description: formatMessage(messages.removeErrorDescription),
					type: 'error',
					close: 'auto',
				});
			}
		},
		[cloudId, removeTrack, flags, formatMessage],
	);

	return (
		<Flex direction="column">
			{playlistItems.length === 0 ? (
				<PlaylistEmptyView />
			) : (
				<PlaylistView
					items={playlistItems}
					currentItem={currentItem}
					onItemClick={handleItemClick}
					onRemove={handleRemoveItem}
					audioRef={audioRef}
				/>
			)}
		</Flex>
	);
};

type PlaylistProps = {
	cloudId: string;
	defaultExpanded?: boolean;
	audioRef: RefObject<HTMLAudioElement>;
	flags?: FlagsContainer;
};

export const Playlist = ({
	cloudId,
	defaultExpanded,
	audioRef,
	flags,
}: PlaylistProps): React.JSX.Element => {
	const { pendingContent } = useAudioPlayerStore();
	const [setFetchKeyCallback, setSetFetchKeyCallback] = useState<(() => void) | null>(null);

	return (
		<PlaylistWrapper
			defaultExpanded={defaultExpanded}
			hasOtherActivePlayback={pendingContent !== null}
			audioRef={audioRef}
			onExpand={setFetchKeyCallback || undefined}
		>
			<ErrorBoundary
				fallbackRender={(props) => <PlaylistErrorView {...props} onRetry={() => {}} />}
			>
				<Suspense fallback={<PlaylistLoadingView />}>
					<PlaylistInner
						cloudId={cloudId}
						audioRef={audioRef}
						flags={flags}
						onSetFetchKeyCallback={setSetFetchKeyCallback}
					/>
				</Suspense>
			</ErrorBoundary>
		</PlaylistWrapper>
	);
};
